import { actionProps } from "@/lib/controlAnalytics";
import { FileIcon } from "@/components/icons";
import { formatBytes } from "@/lib/format";
import { useOpenTailored, useTailoredLinks } from "@/lib/hooks/resume";
import { stamp, type RecordEntry, type RecordTone } from "@/lib/posting";

const DOT: Record<RecordTone, string> = {
    board: "bg-line-strong",
    match: "bg-green",
    queue: "bg-info",
    tailor: "bg-lime",
    sent: "bg-green",
    fault: "bg-negative",
    quiet: "bg-line-strong",
};

/**
 * The record: every event from the catalogue and from envoy, oldest
 * first, each with its kind and its place in its own record. `compact`
 * is the drawer's: the kind and when, no detail.
 */
export default function RecordList({
    entries,
    compact = false,
}: {
    entries: readonly RecordEntry[];
    compact?: boolean;
}) {
    const links = useTailoredLinks(
        compact ? [] : entries.flatMap((entry) => (entry.resumeId ? [entry.resumeId] : [])),
    );
    const openPdf = useOpenTailored();

    if (entries.length === 0) {
        const text = compact ? "text-[14px] leading-5" : "text-ui";
        return <p className={`m-0 ${text} text-fg-faint`}>Nothing recorded yet.</p>;
    }

    return (
        <ol className="m-0 flex list-none flex-col p-0">
            {entries.map((entry, index) => {
                const last = index === entries.length - 1;
                const link = entry.resumeId ? links[entry.resumeId] : undefined;
                return (
                    <li
                        key={entry.id}
                        className={`grid grid-cols-[18px_minmax(0,1fr)] ${compact ? "gap-2.5 py-1.5" : "gap-3 py-2"}`}
                    >
                        <span className="flex flex-col items-center">
                            <span
                                className={`size-2.5 rounded-full ${compact ? "mt-1" : "mt-[5px] outline outline-1 -outline-offset-1 outline-line-strong"} ${DOT[entry.tone]}`}
                                aria-hidden="true"
                            />
                            {!last && (
                                <span
                                    className="mt-1 w-0.5 flex-1 bg-line-soft"
                                    aria-hidden="true"
                                />
                            )}
                        </span>
                        {compact ? (
                            <span className="flex flex-col gap-px">
                                <span className="text-[14px] leading-5 font-medium">
                                    {entry.kind}
                                </span>
                                <span className="text-[12px] leading-4 text-fg-faint">
                                    {stamp(entry.at)} · {entry.service}
                                </span>
                            </span>
                        ) : (
                            <span className="flex min-w-0 flex-col gap-1">
                                <span className="flex flex-wrap items-baseline gap-2">
                                    <span className="text-ui font-semibold">{entry.kind}</span>
                                    <span className="text-small text-fg-faint">
                                        {stamp(entry.at)}
                                    </span>
                                    <span className="ml-auto font-mono text-small text-fg-faint">
                                        #{entry.seq} {entry.service}
                                    </span>
                                </span>
                                {entry.detail && (
                                    <span className="text-ui leading-normal text-fg-muted [text-wrap:pretty]">
                                        {entry.detail}
                                    </span>
                                )}
                                {entry.resumeId && (
                                    <button
                                        {...actionProps("tailored_pdf_open_clicked", {
                                            source: "RecordList",
                                            resume_id: entry.resumeId,
                                        })}
                                        type="button"
                                        onClick={() => openPdf.mutate(entry.resumeId as string)}
                                        className="inline-flex items-center gap-1.5 self-start rounded-full border border-line bg-card-raised px-[9px] py-[3px] text-small font-semibold text-fg hover:text-brand-ink"
                                    >
                                        <FileIcon className="size-3 text-fg-faint" />
                                        Tailored resume
                                        {link ? ` · ${formatBytes(link.byte_size)}` : ""}
                                    </button>
                                )}
                            </span>
                        )}
                    </li>
                );
            })}
        </ol>
    );
}
